import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../../contexts/StripeContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import stripePaymentService from '../../../services/stripePaymentService';


const StripePaymentFormInner = ({ clientSecret, invoice, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/billing-subscription-management`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Payment failed. Please try again.');
        onError?.(stripeError);
      } else if (paymentIntent) {
        if (paymentIntent?.status === 'succeeded') {
          const { data, error } = await stripePaymentService?.confirmPayment(paymentIntent?.id);
          
          // Note: Payment confirmation email is now sent automatically by stripePaymentService.confirmPayment()
          
          setShowSuccessAnimation(true);
          setTimeout(() => {
            setShowSuccessAnimation(false);
            onSuccess?.({
              paymentIntent,
              invoice,
              data,
              warning: error ? 'Payment processed but confirmation failed. Please contact support.' : undefined
            });
          }, 2500);
        } else if (paymentIntent?.status === 'requires_action') {
          setErrorMessage('Additional authentication required. Please complete the verification.');
        } else {
          setErrorMessage('Payment processing incomplete. Please try again.');
        }
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrorMessage(error?.message || 'An unexpected error occurred. Please try again.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Loading payment system...
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">
              Payment Details
            </label>
            <PaymentElement 
              options={{
                fields: {
                  billingDetails: 'auto'
                },
                layout: 'tabs'
              }}
            />
          </div>
        </div>

        {errorMessage && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="AlertCircle" size={18} className="text-error mr-2 mt-0.5" />
              <p className="text-error text-sm">{errorMessage}</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <Icon name="Shield" size={14} className="inline mr-1" />
            Secured by Stripe
          </div>
          <div className="flex space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!paymentReady || isProcessing || !stripe || !elements}
            >
              {isProcessing ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="CreditCard" size={16} className="mr-2" />
                  Pay K{invoice?.amountDue?.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>

      {showSuccessAnimation && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
          <div className="text-center space-y-4 p-8">
            <div className="relative">
              <div className="w-16 h-16 mx-auto bg-success/20 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                  <Icon name="Check" size={24} className="text-white" />
                </div>
              </div>
              <div className="absolute inset-0 w-16 h-16 mx-auto border-4 border-success/30 rounded-full animate-ping"></div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-success">Payment Successful!</h3>
              <p className="text-sm text-muted-foreground">Your invoice has been paid</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StripePaymentModal = ({ invoice, customerInfo, onSuccess, onError, onClose }) => {
  const { stripePromise, stripeOptions } = useStripeContext();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    createPaymentIntent();
  }, [invoice]);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      setError('');

      const invoiceData = {
        invoiceId: invoice?.id,
        amount: invoice?.amountDue,
        currency: invoice?.currency || 'PGK',
        description: `Subscription Invoice ${invoice?.invoiceNumber}`,
        billingPeriodStart: invoice?.billingPeriodStart,
        billingPeriodEnd: invoice?.billingPeriodEnd
      };

      const result = await stripePaymentService?.createPaymentIntent(invoiceData, customerInfo);
      setClientSecret(result?.clientSecret);
    } catch (err) {
      console.error('Payment intent creation error:', err);
      setError(err?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) {
    return null;
  }

  const elementsOptions = clientSecret ? {
    clientSecret,
    ...stripeOptions,
    defaultValues: customerInfo ? {
      billingDetails: {
        name: customerInfo?.name || '',
        email: customerInfo?.email || '',
        phone: customerInfo?.phone || '',
        address: {
          line1: customerInfo?.address?.line1 || '',
          city: customerInfo?.address?.city || '',
          state: customerInfo?.address?.state || '',
          postal_code: customerInfo?.address?.postal_code || '',
          country: customerInfo?.address?.country || 'PG',
        }
      }
    } : undefined
  } : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">Pay Invoice</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Invoice #{invoice?.invoiceNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Preparing payment...</p>
            </div>
          ) : error ? (
            <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
              <p className="text-error font-medium mb-2">Payment Initialization Failed</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={createPaymentIntent} variant="outline">
                Try Again
              </Button>
            </div>
          ) : clientSecret ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <StripePaymentFormInner
                clientSecret={clientSecret}
                invoice={invoice}
                onSuccess={onSuccess}
                onError={onError}
                onCancel={onClose}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StripePaymentModal;